import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/lib/api/client';
import type { AccountRequest, AccountRequestUpdate } from './types';

/**
 * Account requests — people asking for an account, before there is one.
 *
 * Five endpoints, all under `oauth/account`. The original ran each of them as
 * an inline axios call with its own `setLoading(true)` / `setLoading(false)`
 * pair and its own copy of the error handling, then re-ran the search by
 * calling `search(searchTerm)` from inside the callback — which is why the
 * list sometimes refreshed against a stale term. Here the list is a query, the
 * four writes are mutations, and every one of them invalidates the list.
 *
 * Bodies are PascalCase, matching the rest of the admin API in this app; the
 * server binds case-insensitively, and the swagger model names the same fields
 * in camelCase.
 */

const LIST_KEY = ['account-requests'] as const;

export function useAccountRequests(query: string, type: number) {
  const trimmed = query.trim();

  return useQuery({
    queryKey: [...LIST_KEY, 'list', type, trimmed],
    queryFn: () =>
      post<AccountRequest[]>('oauth/account/requests', {
        Query: trimmed,
        Type: type,
      }),
    // Unlike the customer search this endpoint lists everything for an empty
    // term, so the page opens with the pending queue already on screen.
    staleTime: 30_000,
  });
}

/** Anything that changes a request changes where it sits in the list. */
function useInvalidateRequests() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: LIST_KEY });
}

export function useApproveRequest() {
  const invalidate = useInvalidateRequests();

  return useMutation({
    mutationFn: (id: string) =>
      post<unknown>(`oauth/account/request/approve/${id}`, {}),
    onSuccess: invalidate,
  });
}

export function useRejectRequest() {
  const invalidate = useInvalidateRequests();

  return useMutation({
    mutationFn: ({ id, message }: { id: string; message: string }) =>
      post<unknown>('oauth/account/request/reject', {
        AccountRequestId: id,
        Message: message,
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateRequest() {
  const invalidate = useInvalidateRequests();

  return useMutation({
    mutationFn: (values: AccountRequestUpdate) =>
      put<unknown>('oauth/account/request', values),
    onSuccess: invalidate,
  });
}

/**
 * The sign-up link an approved applicant is emailed, for when that email never
 * arrived and support has to read it out or paste it into a chat.
 */
export function useApprovalLink() {
  return useMutation({
    mutationFn: (id: string) => get<string>(`oauth/account/approve/link/${id}`),
  });
}
